import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth, UserButton } from "@clerk/react";
import { Clock } from "lucide-react";
import { api, errMsg, setTokenGetter } from "@/lib/api";

const MeCtx = createContext(null);
export const useMe = () => useContext(MeCtx);

const PendingAccess = ({ me }) => (
  <div className="min-h-screen flex items-center justify-center p-6" data-testid="pending-access-screen">
    <div className="max-w-md w-full border border-slate-200 bg-white rounded-md p-8">
      <div className="flex items-center justify-between mb-6">
        <Clock className="h-6 w-6 text-amber-600" />
        <UserButton />
      </div>
      <h1 className="text-xl font-semibold text-slate-900">{me.status === "requested" ? "You haven't been added yet" : "No access"}</h1>
      <p className="text-sm text-slate-600 mt-2">
        You are signed in as <span className="font-mono">{me.email}</span>. {me.status === "requested"
          ? "Only people added by an admin can use the Tessol Platform. Your request has been recorded — ask your admin to add you."
          : "Your account is inactive or has no role. Ask your admin to activate it."}
      </p>
    </div>
  </div>
);

export function MeProvider({ children }) {
  const { getToken } = useAuth();
  const [me, setMe] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setTokenGetter(() => getToken());
    api.get("/me").then((r) => setMe(r.data)).catch((e) => setError(errMsg(e)));
  }, [getToken]);

  const can = useCallback((p) => !!me?.permissions?.includes(p), [me]);
  const value = useMemo(() => ({ me, can }), [me, can]);
  if (error) return <div className="p-8 text-sm text-red-600" data-testid="me-error">{error}</div>;
  if (!me) return <div className="p-8 text-sm text-slate-500" data-testid="me-loading">Loading…</div>;
  if (!me.active || !me.roles.length) return <PendingAccess me={me} />;
  return <MeCtx.Provider value={value}>{children}</MeCtx.Provider>;
}
